import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

class LocalStateChangeEvent extends Event {
  constructor(
    public readonly key: string,
    public readonly newValue: any,
  ) {
    super('LocalStateChangeEvent');
  }
}

/**
 * Чистое чтение состояния из localStorage с валидацией zod-схемой.
 * Логика 1:1 с внутренним getInitialValue хука — вынесена для переиспользования
 * извне React-дерева (см. showGetStartedWidget).
 */
export function readLocalStorageBackedState<T>(
  key: string,
  zodSchema: z.ZodType<T, any, any>,
  defaultValue: T,
): T {
  const value = localStorage.getItem(key);
  if (!value) return defaultValue;
  let jsonParsedValue;
  try {
    jsonParsedValue = JSON.parse(value);
  } catch {
    return defaultValue;
  }

  const zodParseResult = zodSchema.safeParse(jsonParsedValue);
  if (!zodParseResult.success) return defaultValue;
  return zodParseResult.data;
}

/**
 * Чистая запись состояния в localStorage + dispatch LocalStateChangeEvent.
 * Позволяет обновить состояние императивно извне React-дерева и мгновенно
 * синхронизировать его со всеми смонтированными useLocalStorageBackedState.
 */
export function writeLocalStorageBackedState<T>(key: string, value: T): void {
  localStorage.setItem(key, JSON.stringify(value));
  globalThis.dispatchEvent(new LocalStateChangeEvent(key, value));
}

export function useLocalStorageBackedState<T>(
  defaultValue: T,
  zodSchema: z.ZodType<T, any, any>,
  localStoreKey: string,
): [T, Dispatch<SetStateAction<T>>] {
  const getInitialValue = useCallback(
    () => readLocalStorageBackedState(localStoreKey, zodSchema, defaultValue),
    [defaultValue, localStoreKey, zodSchema],
  );

  const [innerValue, setInnerValue] = useState<T>(getInitialValue());

  const updateFromUser = useCallback(
    (newValue: SetStateAction<T>) => {
      let actualNewValue;
      if (typeof newValue === 'function') {
        actualNewValue = (newValue as (prevValue: T) => T)(innerValue);
      } else {
        actualNewValue = newValue;
      }

      writeLocalStorageBackedState(localStoreKey, actualNewValue);
    },
    [localStoreKey, innerValue],
  );

  // updateFromEvent
  useEffect(() => {
    const handler = (e: Event) => {
      if (e instanceof LocalStateChangeEvent && e.key === localStoreKey) {
        const newValue = e.newValue;
        const zodParseResult = zodSchema.safeParse(newValue);
        if (!zodParseResult.success) return;
        setInnerValue(zodParseResult.data);
      }
    };
    globalThis.addEventListener('LocalStateChangeEvent', handler);
    return () => {
      globalThis.removeEventListener('LocalStateChangeEvent', handler);
    };
  }, [localStoreKey, zodSchema]);

  return [innerValue, updateFromUser];
}
